// src/contexts/CandidateProfileContext.tsx - FIXED with role check
// This context handles ALL candidate-specific data (profile + documents)
// Only imported and used in candidate-facing components

import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { CandidateProfileService } from '@/services/CandidateProfileService';
import { Document } from '@/lib/api/documentApi';
import {
    CandidateProfile,
    CandidateProfileUpdatePayload
} from '@/lib/models/candidateProfile';
import { useAuth } from '@/contexts/AuthContext';

interface CandidateProfileContextType {
    // Profile Data & States
    userProfile: CandidateProfile | null;
    isLoadingProfile: boolean;
    isSavingProfile: boolean;
    profileError: string | null;

    // Document Data & States
    profileDocuments: Document[];
    isLoadingDocuments: boolean;
    isUploadingDocument: boolean;
    documentError: string | null;

    // Profile functions
    getUserProfile: () => Promise<CandidateProfile | null>;
    updateUserProfile: (payload: CandidateProfileUpdatePayload) => Promise<CandidateProfile | null>;
    createUserProfile: (payload: CandidateProfileUpdatePayload) => Promise<CandidateProfile | null>;
    deleteUserProfile: () => Promise<void>;

    // Document functions
    getProfileDocuments: () => Promise<Document[]>;
    uploadDocument: (documentType: 'cv' | 'exam_certificate' | 'grade_certificate' | 'other', file: File) => Promise<Document>;
    createTextDocument: (documentType: string, content: string) => Promise<Document>;
    deleteDocument: (documentId: string) => Promise<void>;
    getDocumentDownloadUrl: (documentId: string) => string;

    // Refresh functions
    refreshUserProfile: () => Promise<void>;
    refreshDocuments: () => Promise<void>;
}

const CandidateProfileContext = createContext<CandidateProfileContextType | undefined>(undefined);

export const CandidateProfileProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user, isAuthenticated } = useAuth();

    // Profile state
    const [userProfile, setUserProfile] = useState<CandidateProfile | null>(null);
    const [isLoadingProfile, setIsLoadingProfile] = useState(false);
    const [isSavingProfile, setIsSavingProfile] = useState(false);
    const [profileError, setProfileError] = useState<string | null>(null);

    // Document state
    const [profileDocuments, setProfileDocuments] = useState<Document[]>([]);
    const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);
    const [isUploadingDocument, setIsUploadingDocument] = useState(false);
    const [documentError, setDocumentError] = useState<string | null>(null);

    // ✅ FIXED: Only load profile for CANDIDATE users
    useEffect(() => {
        if (isAuthenticated && user?.id) {
            // ✅ CRITICAL: Check role before loading profile!
            if (user.role === 'candidate') {
                console.log('👤 [CandidateProfile] Candidate user authenticated, loading profile...');
                getUserProfile();
            } else {
                console.log(`👤 [CandidateProfile] Non-candidate user (${user.role}) detected, skipping profile load`);
                setUserProfile(null);
                setProfileDocuments([]);
            }
        } else {
            console.log('👤 [CandidateProfile] User not authenticated, clearing profile data...');
            setUserProfile(null);
            setProfileDocuments([]);
        }
    }, [isAuthenticated, user?.id, user?.role]);

    // ✅ Load documents when profile changes
    useEffect(() => {
        if (userProfile?.id) {
            console.log('📄 [CandidateProfile] Profile loaded, fetching documents...');
            getProfileDocuments();
        } else {
            console.log('📄 [CandidateProfile] No profile, clearing documents...');
            setProfileDocuments([]);
        }
    }, [userProfile?.id]);

    // ==================== PROFILE FUNCTIONS ====================

    const getUserProfile = useCallback(async (): Promise<CandidateProfile | null> => {
        if (!user?.id) {
            console.log('⚠️ [CandidateProfile] No user ID available');
            return null;
        }

        // ✅ Double-check role before API call
        if (user.role !== 'candidate') {
            console.log('⚠️ [CandidateProfile] getUserProfile called for non-candidate, skipping');
            return null;
        }

        setIsLoadingProfile(true);
        setProfileError(null);

        try {
            console.log('📥 [CandidateProfile] Fetching profile for user:', user.id);
            const profile = await CandidateProfileService.getUserProfile(user.id);

            if (profile) {
                setUserProfile(profile);
                console.log('✅ [CandidateProfile] Profile loaded:', profile.id);
                return profile;
            } else {
                console.log('ℹ️ [CandidateProfile] No profile found (not created yet)');
                setUserProfile(null);
                return null;
            }
        } catch (error: any) {
            console.error('❌ [CandidateProfile] Error loading profile:', error);

            // ✅ 404 is not an error - profile just doesn't exist yet
            if (error?.statusCode === 404) {
                console.log('ℹ️ [CandidateProfile] Profile not found (404) - hasn\'t been created');
                setUserProfile(null);
                setProfileError(null);
                return null;
            }

            // ✅ For other errors, set error state
            setProfileError('Failed to load profile');
            return null;
        } finally {
            setIsLoadingProfile(false);
        }
    }, [user?.id, user?.role]);

    const updateUserProfile = useCallback(async (payload: CandidateProfileUpdatePayload): Promise<CandidateProfile | null> => {
        if (!user?.id) {
            console.log('⚠️ [CandidateProfile] No user ID for update');
            return null;
        }

        if (user.role !== 'candidate') {
            console.log('⚠️ [CandidateProfile] updateUserProfile called for non-candidate');
            return null;
        }

        setIsSavingProfile(true);
        setProfileError(null);

        try {
            console.log('💾 [CandidateProfile] Updating profile for user:', user.id);
            const updatedProfile = await CandidateProfileService.updateUserProfile(user.id, payload);

            setUserProfile(updatedProfile);
            console.log('✅ [CandidateProfile] Profile updated successfully');
            return updatedProfile;
        } catch (error: any) {
            console.error('❌ [CandidateProfile] Error updating profile:', error);
            setProfileError(error?.message || 'Failed to update profile');
            return null;
        } finally {
            setIsSavingProfile(false);
        }
    }, [user?.id, user?.role]);

    const createUserProfile = useCallback(async (payload: CandidateProfileUpdatePayload): Promise<CandidateProfile | null> => {
        if (!user?.id) {
            console.log('⚠️ [CandidateProfile] No user ID for creation');
            return null;
        }

        if (user.role !== 'candidate') {
            console.log('⚠️ [CandidateProfile] createUserProfile called for non-candidate');
            return null;
        }

        setIsSavingProfile(true);
        setProfileError(null);

        try {
            console.log('✨ [CandidateProfile] Creating profile for user:', user.id);
            const newProfile = await CandidateProfileService.createUserProfile(payload);

            setUserProfile(newProfile);
            console.log('✅ [CandidateProfile] Profile created successfully');
            return newProfile;
        } catch (error: any) {
            console.error('❌ [CandidateProfile] Error creating profile:', error);
            setProfileError(error?.message || 'Failed to create profile');
            return null;
        } finally {
            setIsSavingProfile(false);
        }
    }, [user?.id, user?.role]);

    const deleteUserProfile = useCallback(async (): Promise<void> => {
        if (!user?.id) {
            console.log('⚠️ [CandidateProfile] No user ID for deletion');
            return;
        }

        if (user.role !== 'candidate') {
            console.log('⚠️ [CandidateProfile] deleteUserProfile called for non-candidate');
            return;
        }

        setIsSavingProfile(true);
        setProfileError(null);

        try {
            console.log('🗑️ [CandidateProfile] Deleting profile for user:', user.id);
            await CandidateProfileService.deleteUserProfile(user.id);

            setUserProfile(null);
            setProfileDocuments([]);
            console.log('✅ [CandidateProfile] Profile deleted successfully');
        } catch (error: any) {
            console.error('❌ [CandidateProfile] Error deleting profile:', error);
            setProfileError(error?.message || 'Failed to delete profile');
            throw error;
        } finally {
            setIsSavingProfile(false);
        }
    }, [user?.id, user?.role]);

    // ==================== DOCUMENT FUNCTIONS ====================

    const getProfileDocuments = useCallback(async (): Promise<Document[]> => {
        if (!userProfile?.id) {
            console.log('⚠️ [CandidateProfile] No profile ID for documents');
            return [];
        }

        setIsLoadingDocuments(true);
        setDocumentError(null);

        try {
            console.log('📄 [CandidateProfile] Fetching documents for profile:', userProfile.id);
            const docs = await CandidateProfileService.getProfileDocuments(userProfile.id);

            setProfileDocuments(docs);
            console.log(`✅ [CandidateProfile] Loaded ${docs.length} documents`);
            return docs;
        } catch (error: any) {
            console.error('❌ [CandidateProfile] Error fetching documents:', error);
            setDocumentError('Failed to load documents');
            return [];
        } finally {
            setIsLoadingDocuments(false);
        }
    }, [userProfile?.id]);

    const uploadDocument = useCallback(async (
        documentType: 'cv' | 'exam_certificate' | 'grade_certificate' | 'other',
        file: File
    ): Promise<Document> => {
        if (!userProfile?.id) {
            throw new Error('No profile ID available for document upload');
        }

        setIsUploadingDocument(true);
        setDocumentError(null);

        try {
            console.log('📤 [CandidateProfile] Uploading document:', file.name);
            const doc = await CandidateProfileService.uploadDocument(userProfile.id, documentType, file);

            // The backend keeps exactly ONE document per (entity, document_type) —
            // CreateFileDocument replaces any existing doc of the same type. Mirror
            // that here: drop the replaced doc rather than appending, so the UI can't
            // briefly show two CVs (or two of any single-slot document type).
            setProfileDocuments(prev => [...prev.filter(d => d.document_type !== documentType), doc]);
            console.log('✅ [CandidateProfile] Document uploaded successfully');
            return doc;
        } catch (error: any) {
            console.error('❌ [CandidateProfile] Error uploading document:', error);
            setDocumentError(error?.message || 'Failed to upload document');
            throw error;
        } finally {
            setIsUploadingDocument(false);
        }
    }, [userProfile?.id]);

    const createTextDocument = useCallback(async (documentType: string, content: string): Promise<Document> => {
        if (!userProfile?.id) {
            throw new Error('No profile ID available for document creation');
        }

        setIsUploadingDocument(true);
        setDocumentError(null);

        try {
            console.log('📝 [CandidateProfile] Creating text document');
            const doc = await CandidateProfileService.createTextDocument(userProfile.id, documentType, content);

            // One document per (entity, document_type) — replace, don't append (see uploadDocument).
            setProfileDocuments(prev => [...prev.filter(d => d.document_type !== documentType), doc]);
            console.log('✅ [CandidateProfile] Text document created successfully');
            return doc;
        } catch (error: any) {
            console.error('❌ [CandidateProfile] Error creating text document:', error);
            setDocumentError(error?.message || 'Failed to create document');
            throw error;
        } finally {
            setIsUploadingDocument(false);
        }
    }, [userProfile?.id]);

    const deleteDocument = useCallback(async (documentId: string): Promise<void> => {
        setDocumentError(null);

        try {
            console.log('🗑️ [CandidateProfile] Deleting document:', documentId);
            await CandidateProfileService.deleteDocument(documentId);

            setProfileDocuments(prev => prev.filter(doc => doc.id !== documentId));
            console.log('✅ [CandidateProfile] Document deleted successfully');
        } catch (error: any) {
            console.error('❌ [CandidateProfile] Error deleting document:', error);
            setDocumentError(error?.message || 'Failed to delete document');
            throw error;
        }
    }, []);

    const getDocumentDownloadUrl = useCallback((documentId: string): string => {
        return CandidateProfileService.getDocumentDownloadUrl(documentId);
    }, []);

    // ==================== REFRESH FUNCTIONS ====================

    const refreshUserProfile = useCallback(async (): Promise<void> => {
        console.log('🔄 [CandidateProfile] Refreshing profile...');
        await getUserProfile();
    }, [getUserProfile]);

    const refreshDocuments = useCallback(async (): Promise<void> => {
        console.log('🔄 [CandidateProfile] Refreshing documents...');
        await getProfileDocuments();
    }, [getProfileDocuments]);

    // ==================== CONTEXT VALUE ====================

    const value: CandidateProfileContextType = {
        // Profile data
        userProfile,
        isLoadingProfile,
        isSavingProfile,
        profileError,

        // Document data
        profileDocuments,
        isLoadingDocuments,
        isUploadingDocument,
        documentError,

        // Profile functions
        getUserProfile,
        updateUserProfile,
        createUserProfile,
        deleteUserProfile,

        // Document functions
        getProfileDocuments,
        uploadDocument,
        createTextDocument,
        deleteDocument,
        getDocumentDownloadUrl,

        // Refresh functions
        refreshUserProfile,
        refreshDocuments,
    };

    return (
        <CandidateProfileContext.Provider value={value}>
            {children}
        </CandidateProfileContext.Provider>
    );
};

// ==================== HOOK ====================

export const useCandidateProfile = (): CandidateProfileContextType => {
    const context = useContext(CandidateProfileContext);
    if (context === undefined) {
        throw new Error('useCandidateProfile must be used within a CandidateProfileProvider');
    }
    return context;
};